import I18n from 'I18n';
import UIDropdown from 'UIComponents/dropdown/UIDropdown';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { useAsyncGenericChannelsWithCapabilities } from 'composer-data/channel-capabilities/public/hooks';
import { EMAIL_GENERIC_CHANNEL_ID, FB_MESSENGER_GENERIC_CHANNEL_ID, LIVE_CHAT_GENERIC_CHANNEL_ID, SMS_GENERIC_CHANNEL_ID, WHATSAPP_GENERIC_CHANNEL_ID } from 'conversations-message-history/common-message-format/constants/genericChannelIds';
import { isConfigKeyGenericChannelId } from 'conversations-thread-data/channel-configuration/public/operators';
import { isGenericChannelId } from 'conversations-thread-data/channel-configuration/public/types';
import { AccessLevelContextProvider } from 'customer-data-properties/accessLevel/AccessLevelContext';
import { memo, useCallback, useEffect, useState } from 'react';
import { ChannelLabel } from '../../../capability-editor/public/components';
import { getUrlFragmentFromConfigKey } from '../../../editor-utilities/protected/utils';
import { ChannelDropdownOptions } from './ChannelDropdownOptions';
import { SelectedChannel } from './SelectedChannel';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function getIconForGenericChannelId(genericChannelId) {
  switch (genericChannelId) {
    case LIVE_CHAT_GENERIC_CHANNEL_ID:
      return 'messages';
    case FB_MESSENGER_GENERIC_CHANNEL_ID:
      return 'socialFacebookMessenger';
    case EMAIL_GENERIC_CHANNEL_ID:
      return 'email';
    case WHATSAPP_GENERIC_CHANNEL_ID:
      return 'socialWhatsapp';
    case SMS_GENERIC_CHANNEL_ID:
      return 'sms';
    default:
      return 'messages';
  }
}
function ChannelTab({
  isSelected,
  onClick,
  selectedChannelId,
  trailingIcon = null,
  disabled
}) {
  const icon = getIconForGenericChannelId(selectedChannelId);
  return /*#__PURE__*/_jsxs(SelectedChannel, {
    use: "primary-white",
    onClick: onClick,
    "data-test-id": "HUBSPOT-dropdown-trigger",
    "data-channel-id": selectedChannelId,
    "is-selected": `${isSelected}`,
    disabled: disabled,
    children: [icon ? /*#__PURE__*/_jsx(UIIcon, {
      name: icon
    }) : null, /*#__PURE__*/_jsx("span", {
      "data-test-id": "selected-channel-label",
      children: /*#__PURE__*/_jsx(ChannelLabel, {
        genericChannelId: selectedChannelId
      })
    }), trailingIcon]
  });
}
ChannelTab.displayName = 'ChannelTab';
const ChannelSwitchDropdown = ({
  allowedMessageChannelIds,
  associatedObjectId,
  associatedObjectTypeId,
  callingSource,
  currentChannelId,
  defaultChannelId,
  hasCalling,
  isKnownVisitor,
  onSelectedChange = () => {},
  subjectId,
  inboxId,
  threadId
}) => {
  const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
  const [selectedChannelId, setSelectedChannelId] = useState(defaultChannelId);
  const isSelected = isGenericChannelId(currentChannelId);
  const closeDropdown = useCallback(() => {
    setDropdownIsOpen(false);
  }, []);
  useEffect(() => {
    // when default channel id has changed (i.e. switching threads), reset the selected state
    setSelectedChannelId(defaultChannelId);
  }, [defaultChannelId]);
  useEffect(() => {
    if (isSelected) {
      setSelectedChannelId(currentChannelId);
    } else {
      setDropdownIsOpen(false);
    }
  }, [isSelected, currentChannelId]);

  // when switching from comments to a channel, the evt is a number || string
  // when switching channels from within the dropdown,
  // the evt will have a currentTarget & value
  const selectChannel = useCallback(evt => {
    const channel = typeof evt === 'number' || typeof evt === 'string' ? evt : evt.currentTarget && evt.currentTarget.value;
    if (!channel) return;
    const genericChannelId = isConfigKeyGenericChannelId(channel) && typeof channel === 'string' ? parseInt(channel, 10) : channel;
    closeDropdown();
    setSelectedChannelId(genericChannelId);
    const fragment = getUrlFragmentFromConfigKey({
      editorConfigKey: genericChannelId
    });
    onSelectedChange(fragment);
  }, [closeDropdown, onSelectedChange]);
  const switchOrOpenDropdown = () => {
    if (!isSelected) {
      selectChannel(selectedChannelId);
    } else {
      setDropdownIsOpen(!dropdownIsOpen);
    }
  };
  const ChannelTabWithTooltip = () => {
    const {
      loading,
      error
    } = useAsyncGenericChannelsWithCapabilities();
    const disableTab = Boolean(loading || error);
    return /*#__PURE__*/_jsx(UITooltip, {
      title: I18n.text('composer-ui.tabbed-editor.channel-switch-dropdown.tooltip-text'),
      disabled: dropdownIsOpen,
      children: /*#__PURE__*/_jsx(ChannelTab, {
        isSelected: isSelected,
        onClick: switchOrOpenDropdown,
        selectedChannelId: selectedChannelId,
        trailingIcon: isSelected ? /*#__PURE__*/_jsx(UIIcon, {
          name: "downCarat",
          size: 10
        }) : null,
        disabled: disableTab
      })
    });
  };
  ChannelTabWithTooltip.displayName = 'ChannelTabWithTooltip';
  return allowedMessageChannelIds.length === 1 && !hasCalling ? /*#__PURE__*/_jsx(ChannelTab, {
    isSelected: isSelected,
    onClick: () => selectChannel(defaultChannelId),
    selectedChannelId: selectedChannelId
  }) : /*#__PURE__*/_jsx(AccessLevelContextProvider, {
    children: /*#__PURE__*/_jsx(UIDropdown, {
      placement: "top",
      Button: ChannelTabWithTooltip,
      arrowSize: "small",
      animateOnToggle: true,
      closeOnMenuClick: false,
      open: dropdownIsOpen,
      caretRenderer: null,
      onOpenChange: switchOrOpenDropdown,
      children: /*#__PURE__*/_jsx(ChannelDropdownOptions, {
        allowedMessageChannelIds: allowedMessageChannelIds,
        closeDropdown: closeDropdown,
        isSelected: isSelected,
        callingSource: callingSource,
        associatedObjectId: associatedObjectId,
        associatedObjectTypeId: associatedObjectTypeId,
        hasCalling: hasCalling,
        isKnownVisitor: isKnownVisitor,
        selectChannel: selectChannel,
        subjectId: subjectId,
        inboxId: inboxId,
        threadId: threadId
      })
    })
  });
};
ChannelSwitchDropdown.displayName = 'ChannelSwitchDropdown';
export default /*#__PURE__*/memo(ChannelSwitchDropdown);